/**
 * usePermissionChecker - React hook for permission checking
 *
 * This hook provides a clean interface to check permissions using the PermissionManager
 * and can be easily used in any React component.
 */

import { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/setup";
import {
  PermissionManager,
  createPermissionManager,
  PermissionContext,
  PermissionResource,
  PermissionAction,
  PERMISSION_RESOURCES,
  PERMISSION_ACTIONS,
} from "@/__core/permissions/utils/PermissionManager";

export interface UsePermissionCheckerReturn {
  // Core permission checking methods
  hasPermission: (resource: string, action: string) => boolean;
  canRead: (resource: string) => boolean;
  canCreate: (resource: string) => boolean;
  canUpdate: (resource: string) => boolean;
  canDelete: (resource: string) => boolean;

  // Role checking
  hasRole: (roleName: string) => boolean;

  // Permission summary
  getPermissionSummary: (resource: string) => {
    canRead: boolean;
    canCreate: boolean;
    canUpdate: boolean;
    canDelete: boolean;
  };

  // Resource permissions
  getResourcePermissions: (resource: string) => string[];

  // Multiple permissions checking
  hasAnyPermission: (
    permissions: Array<{ resource: string; action: string }>
  ) => boolean;
  hasAllPermissions: (
    permissions: Array<{ resource: string; action: string }>
  ) => boolean;

  // Permission manager instance
  permissionManager: PermissionManager;
}

export const usePermissionChecker = (): UsePermissionCheckerReturn => {
  const user = useSelector((state: RootState) => state.auth.user);
  const globalRoles = useSelector((state: RootState) => state.auth.globalRoles);
  const selectedCompany = useSelector(
    (state: RootState) => state.auth.selectedCompany
  );

  // Create permission context
  const permissionContext: PermissionContext = useMemo(() => {
    return {
      user: user
        ? {
            ...user,
            id: String(user.id), // Convert id to string for compatibility
          }
        : { id: "", email: "", username: "" },
      globalRoles: globalRoles || [],
      selectedCompany: selectedCompany || null,
    };
  }, [user, globalRoles, selectedCompany]);

  // Create permission manager instance
  const permissionManager = useMemo(() => {
    return createPermissionManager(permissionContext);
  }, [permissionContext]);

  // Memoized permission checking methods
  const hasPermission = useMemo(() => {
    return (resource: string, action: string) => {
      return permissionManager.hasPermission(resource, action);
    };
  }, [permissionManager]);

  const canRead = useMemo(() => {
    return (resource: string) => {
      return permissionManager.canRead(resource);
    };
  }, [permissionManager]);

  const canCreate = useMemo(() => {
    return (resource: string) => {
      return permissionManager.canCreate(resource);
    };
  }, [permissionManager]);

  const canUpdate = useMemo(() => {
    return (resource: string) => {
      return permissionManager.canUpdate(resource);
    };
  }, [permissionManager]);

  const canDelete = useMemo(() => {
    return (resource: string) => {
      return permissionManager.canDelete(resource);
    };
  }, [permissionManager]);

  const hasRole = useMemo(() => {
    return (roleName: string) => {
      return permissionManager.hasRole(roleName);
    };
  }, [permissionManager]);

  const getPermissionSummary = useMemo(() => {
    return (resource: string) => {
      return permissionManager.getPermissionSummary(resource);
    };
  }, [permissionManager]);

  const getResourcePermissions = useMemo(() => {
    return (resource: string) => {
      return permissionManager.getResourcePermissions(resource);
    };
  }, [permissionManager]);

  const hasAnyPermission = useMemo(() => {
    return (permissions: Array<{ resource: string; action: string }>) => {
      return permissionManager.hasAnyPermission(permissions);
    };
  }, [permissionManager]);

  const hasAllPermissions = useMemo(() => {
    return (permissions: Array<{ resource: string; action: string }>) => {
      return permissionManager.hasAllPermissions(permissions);
    };
  }, [permissionManager]);

  return {
    hasPermission,
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    hasRole,
    getPermissionSummary,
    getResourcePermissions,
    hasAnyPermission,
    hasAllPermissions,
    permissionManager,
  };
};

// Convenience hooks for specific resources (login company scope).
// @deprecated For modules with toolbar company filter, use `useModuleToolbarPermissions` in the parent module and pass permission props to list/form children.

/** @deprecated Use `useModuleToolbarPermissions({ resource: 'users' })` in module parent. */
export const useUsersPermissions = () => {
  const { canRead, canCreate, canUpdate, canDelete, getPermissionSummary } =
    usePermissionChecker();

  return useMemo(
    () => ({
      canRead: canRead(PERMISSION_RESOURCES.USERS),
      canCreate: canCreate(PERMISSION_RESOURCES.USERS),
      canUpdate: canUpdate(PERMISSION_RESOURCES.USERS),
      canDelete: canDelete(PERMISSION_RESOURCES.USERS),
      summary: getPermissionSummary(PERMISSION_RESOURCES.USERS),
    }),
    [canRead, canCreate, canUpdate, canDelete, getPermissionSummary]
  );
};

/** @deprecated Use `useModuleToolbarPermissions({ resource: 'roles' })` in module parent. */
export const useRolesPermissions = () => {
  const {
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    getPermissionSummary,
    permissionManager,
  } = usePermissionChecker();

  return useMemo(() => {
    // Debug API data FIRST before checking permissions
    if (permissionManager) {
      // Try to access the context directly for debugging
      void (permissionManager as any).context;
    }

    const read = canRead(PERMISSION_RESOURCES.ROLES);
    const create = canCreate(PERMISSION_RESOURCES.ROLES);
    const update = canUpdate(PERMISSION_RESOURCES.ROLES);
    const deletePerm = canDelete(PERMISSION_RESOURCES.ROLES);

    return {
      canRead: read,
      canCreate: create,
      canUpdate: update,
      canDelete: deletePerm,
      summary: getPermissionSummary(PERMISSION_RESOURCES.ROLES),
    };
  }, [
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    getPermissionSummary,
    permissionManager,
  ]);
};

export const useCompaniesPermissions = () => {
  const { canRead, canCreate, canUpdate, canDelete, getPermissionSummary } =
    usePermissionChecker();

  return useMemo(
    () => ({
      canRead: canRead(PERMISSION_RESOURCES.COMPANIES),
      canCreate: canCreate(PERMISSION_RESOURCES.COMPANIES),
      canUpdate: canUpdate(PERMISSION_RESOURCES.COMPANIES),
      canDelete: canDelete(PERMISSION_RESOURCES.COMPANIES),
      summary: getPermissionSummary(PERMISSION_RESOURCES.COMPANIES),
    }),
    [canRead, canCreate, canUpdate, canDelete, getPermissionSummary]
  );
};

/** @deprecated Use `useModuleToolbarPermissions` when module has toolbar company filter. */
export const useInsurancePermissions = () => {
  const { canRead, canCreate, canUpdate, canDelete, getPermissionSummary, permissionManager } =
    usePermissionChecker();

  return useMemo(
    () => {
      if (!permissionManager) {
        return {
          canRead: false,
          canCreate: false,
          canUpdate: false,
          canDelete: false,
          summary: {
            canRead: false,
            canCreate: false,
            canUpdate: false,
            canDelete: false,
          },
        };
      }

      const resource = PERMISSION_RESOURCES.EMPLOYEES_INSURANCE;
      return {
        canRead: canRead(resource),
        canCreate: canCreate(resource),
        canUpdate: canUpdate(resource),
        canDelete: canDelete(resource),
        summary: getPermissionSummary(resource),
      };
    },
    [canRead, canCreate, canUpdate, canDelete, getPermissionSummary, permissionManager]
  );
};

/** @deprecated Use `useModuleToolbarPermissions({ resource: PERMISSION_RESOURCES.EMPLOYEES_FIXED_SALARY })` in module parent. */
export const useFixedSalaryPermissions = () => {
  const { canRead, canCreate, canUpdate, canDelete, getPermissionSummary, permissionManager } =
    usePermissionChecker();

  return useMemo(
    () => {
      if (!permissionManager) {
        return {
          canRead: false,
          canCreate: false,
          canUpdate: false,
          canDelete: false,
          summary: {
            canRead: false,
            canCreate: false,
            canUpdate: false,
            canDelete: false,
          },
        };
      }

      const resource = PERMISSION_RESOURCES.EMPLOYEES_FIXED_SALARY;
      return {
        canRead: canRead(resource),
        canCreate: canCreate(resource),
        canUpdate: canUpdate(resource),
        canDelete: canDelete(resource),
        summary: getPermissionSummary(resource),
      };
    },
    [canRead, canCreate, canUpdate, canDelete, getPermissionSummary, permissionManager]
  );
};

/** @deprecated Use `useModuleToolbarPermissions({ resource: 'employees' })` in module parent. */
export const useEmployeesPermissions = () => {
  const { canRead, canCreate, canUpdate, canDelete, getPermissionSummary, permissionManager } =
    usePermissionChecker();

  return useMemo(
    () => {
     

      // Fallback: If permission manager is not available, return false for all permissions
      if (!permissionManager) {
        return {
          canRead: false,
          canCreate: false,
          canUpdate: false,
          canDelete: false,
          summary: {
            canRead: false,
            canCreate: false,
            canUpdate: false,
            canDelete: false,
          },
        };
      }

      const read = canRead(PERMISSION_RESOURCES.EMPLOYEES);
      const create = canCreate(PERMISSION_RESOURCES.EMPLOYEES);
      const update = canUpdate(PERMISSION_RESOURCES.EMPLOYEES);
      const deletePerm = canDelete(PERMISSION_RESOURCES.EMPLOYEES);

      return {
        canRead: read,
        canCreate: create,
        canUpdate: update,
        canDelete: deletePerm,
        summary: getPermissionSummary(PERMISSION_RESOURCES.EMPLOYEES),
      };
    },
    [canRead, canCreate, canUpdate, canDelete, getPermissionSummary, permissionManager]
  );
};

/** @deprecated Use `useModuleToolbarPermissions({ resource: 'transaction' })` in TransactionModule and pass props to list/form. */
export const useFinancePermissions = () => {
  const { canRead, canCreate, canUpdate, canDelete, getPermissionSummary, permissionManager } =
    usePermissionChecker();

  return useMemo(
    () => {
      // Fallback: If permission manager is not available, return false for all permissions
      if (!permissionManager) {
        return {
          canRead: false,
          canCreate: false,
          canUpdate: false,
          canDelete: false,
          summary: {
            canRead: false,
            canCreate: false,
            canUpdate: false,
            canDelete: false,
          },
        };
      }

      const read = canRead(PERMISSION_RESOURCES.TRANSACTION);
      const create = canCreate(PERMISSION_RESOURCES.TRANSACTION);
      const update = canUpdate(PERMISSION_RESOURCES.TRANSACTION);
      const deletePerm = canDelete(PERMISSION_RESOURCES.TRANSACTION);

      return {
        canRead: read,
        canCreate: create,
        canUpdate: update,
        canDelete: deletePerm,
        summary: getPermissionSummary(PERMISSION_RESOURCES.TRANSACTION),
      };
    },
    [canRead, canCreate, canUpdate, canDelete, getPermissionSummary, permissionManager]
  );
};

export const useTransactionTypePermissions = () => {
  const { canRead, canCreate, canUpdate, canDelete, getPermissionSummary, permissionManager } =
    usePermissionChecker();

  return useMemo(
    () => {
      // Fallback: If permission manager is not available, return false for all permissions
      if (!permissionManager) {
        return {
          canRead: false,
          canCreate: false,
          canUpdate: false,
          canDelete: false,
          summary: {
            canRead: false,
            canCreate: false,
            canUpdate: false,
            canDelete: false,
          },
        };
      }

      const read = canRead(PERMISSION_RESOURCES.TRANSACTION_TYPE);
      const create = canCreate(PERMISSION_RESOURCES.TRANSACTION_TYPE);
      const update = canUpdate(PERMISSION_RESOURCES.TRANSACTION_TYPE);
      const deletePerm = canDelete(PERMISSION_RESOURCES.TRANSACTION_TYPE);

      return {
        canRead: read,
        canCreate: create,
        canUpdate: update,
        canDelete: deletePerm,
        summary: getPermissionSummary(PERMISSION_RESOURCES.TRANSACTION_TYPE),
      };
    },
    [canRead, canCreate, canUpdate, canDelete, getPermissionSummary, permissionManager]
  );
};

export const useWalletPermissions = () => {
  const { canRead, canCreate, canUpdate, canDelete, getPermissionSummary, permissionManager } =
    usePermissionChecker();

  return useMemo(
    () => {
      // Fallback: If permission manager is not available, return false for all permissions
      if (!permissionManager) {
        return {
          canRead: false,
          canCreate: false,
          canUpdate: false,
          canDelete: false,
          summary: {
            canRead: false,
            canCreate: false,
            canUpdate: false,
            canDelete: false,
          },
        };
      }

      const read = canRead(PERMISSION_RESOURCES.WALLET);
      const create = canCreate(PERMISSION_RESOURCES.WALLET);
      const update = canUpdate(PERMISSION_RESOURCES.WALLET);
      const deletePerm = canDelete(PERMISSION_RESOURCES.WALLET);

      return {
        canRead: read,
        canCreate: create,
        canUpdate: update,
        canDelete: deletePerm,
        summary: getPermissionSummary(PERMISSION_RESOURCES.WALLET),
      };
    },
    [canRead, canCreate, canUpdate, canDelete, getPermissionSummary, permissionManager]
  );
};

export const useLeadPermissions = () => {
  const { canRead, canCreate, canUpdate, canDelete, getPermissionSummary, permissionManager } =
    usePermissionChecker();

  return useMemo(
    () => {
      // Fallback: If permission manager is not available, return false for all permissions
      if (!permissionManager) {
        return {
          canRead: false,
          canCreate: false,
          canUpdate: false,
          canDelete: false,
          summary: {
            canRead: false,
            canCreate: false,
            canUpdate: false,
            canDelete: false,
          },
        };
      }

      const read = canRead(PERMISSION_RESOURCES.LEAD);
      const create = canCreate(PERMISSION_RESOURCES.LEAD);
      const update = canUpdate(PERMISSION_RESOURCES.LEAD);
      const deletePerm = canDelete(PERMISSION_RESOURCES.LEAD);

      return {
        canRead: read,
        canCreate: create,
        canUpdate: update,
        canDelete: deletePerm,
        summary: getPermissionSummary(PERMISSION_RESOURCES.LEAD),
      };
    },
    [canRead, canCreate, canUpdate, canDelete, getPermissionSummary, permissionManager]
  );
};

export const useContactsPermissions = () => {
  const { canRead, canCreate, canUpdate, canDelete, getPermissionSummary, permissionManager } =
    usePermissionChecker();

  return useMemo(
    () => {
      // Fallback: If permission manager is not available, return false for all permissions
      if (!permissionManager) {
        return {
          canRead: false,
          canCreate: false,
          canUpdate: false,
          canDelete: false,
          summary: {
            canRead: false,
            canCreate: false,
            canUpdate: false,
            canDelete: false,
          },
        };
      }

      const read = canRead(PERMISSION_RESOURCES.CONTACTS);
      const create = canCreate(PERMISSION_RESOURCES.CONTACTS);
      const update = canUpdate(PERMISSION_RESOURCES.CONTACTS);
      const deletePerm = canDelete(PERMISSION_RESOURCES.CONTACTS);

      return {
        canRead: read,
        canCreate: create,
        canUpdate: update,
        canDelete: deletePerm,
        summary: getPermissionSummary(PERMISSION_RESOURCES.CONTACTS),
      };
    },
    [canRead, canCreate, canUpdate, canDelete, getPermissionSummary, permissionManager]
  );
};

export const useViewOthersCustomerPermissions = () => {
  const { canRead, canCreate, canUpdate, canDelete, getPermissionSummary, permissionManager } =
    usePermissionChecker();

  return useMemo(
    () => {
      // Fallback: If permission manager is not available, return false for all permissions
      if (!permissionManager) {
        return {
          canRead: false,
          canCreate: false,
          canUpdate: false,
          canDelete: false,
          summary: {
            canRead: false,
            canCreate: false,
            canUpdate: false,
            canDelete: false,
          },
        };
      }

      const read = canRead(PERMISSION_RESOURCES.VIEW_OTHERS_CUSTOMER);
      const create = canCreate(PERMISSION_RESOURCES.VIEW_OTHERS_CUSTOMER);
      const update = canUpdate(PERMISSION_RESOURCES.VIEW_OTHERS_CUSTOMER);
      const deletePerm = canDelete(PERMISSION_RESOURCES.VIEW_OTHERS_CUSTOMER);

      return {
        canRead: read,
        canCreate: create,
        canUpdate: update,
        canDelete: deletePerm,
        summary: getPermissionSummary(PERMISSION_RESOURCES.VIEW_OTHERS_CUSTOMER),
      };
    },
    [canRead, canCreate, canUpdate, canDelete, getPermissionSummary, permissionManager]
  );
};

export const useViewOthersOrderPermissions = () => {
  const { canRead, canCreate, canUpdate, canDelete, getPermissionSummary, permissionManager } =
    usePermissionChecker();

  return useMemo(
    () => {
      // Fallback: If permission manager is not available, return false for all permissions
      if (!permissionManager) {
        return {
          canRead: false,
          canCreate: false,
          canUpdate: false,
          canDelete: false,
          summary: {
            canRead: false,
            canCreate: false,
            canUpdate: false,
            canDelete: false,
          },
        };
      }

      const read = canRead(PERMISSION_RESOURCES.VIEW_OTHERS_ORDER);
      const create = canCreate(PERMISSION_RESOURCES.VIEW_OTHERS_ORDER);
      const update = canUpdate(PERMISSION_RESOURCES.VIEW_OTHERS_ORDER);
      const deletePerm = canDelete(PERMISSION_RESOURCES.VIEW_OTHERS_ORDER);

      return {
        canRead: read,
        canCreate: create,
        canUpdate: update,
        canDelete: deletePerm,
        summary: getPermissionSummary(PERMISSION_RESOURCES.VIEW_OTHERS_ORDER),
      };
    },
    [canRead, canCreate, canUpdate, canDelete, getPermissionSummary, permissionManager]
  );
};

// Export permission constants for easy access
export { PERMISSION_RESOURCES, PERMISSION_ACTIONS };
export type { PermissionResource, PermissionAction };
